/**
 * 作成画面の{project}の欄（#289、docs/101 §6、Figma ③「案件の選択肢」）。見積書・{deliveryNote}の作成が使う。
 *   欄は「{project}」1つ（{project}名は打たせない）。押すと欄の下に選択肢：
 *     {ongoingProject}（その{customer}の完了していない継続案件・最終 MM/DD）／進行中の{project}（n件 › で開く）／
 *     新規（{subject}と同じ）／{ongoingProject}作成（使う機能「{ongoingProject}」がオンの会社だけ）
 *   値は ProjectChoice（{project}の id／"new"／"ongoing:new"）。既定は画面が GET /groups/default で決める（DB の fn_default_project）
 *   継続案件を選んでいるときは欄の中に札「{ongoingShort}」、完了した継続案件なら札「完了」（作れる＝★B。作った答えに警告）
 */
import { ONGOING_MSG, type GroupListItem, type ProjectChoice } from "@genbaryoku/contract";
import { useEffect, useRef, useState } from "react";
import { t, useLabels } from "../labels.js";
import { useOngoing } from "../lib/ongoing.js";
import { md } from "../lib/num.js";
import { Icon } from "./Shell.jsx";

export function ProjectPicker({
  groups,
  value,
  onChange,
  ongoingName,
  subject,
  loading = false,
  testId = "project-field",
}: {
  /** その{customer}の進行中の{project}（GET /groups?partner=…&state=active の行） */
  groups: GroupListItem[];
  value: ProjectChoice;
  onChange: (v: ProjectChoice, g: GroupListItem | null) => void;
  /** 「{ongoingProject}作成」でできる名前（{customer}の略称＋{ongoingProject}） */
  ongoingName: string;
  /** いま打っている{subject}（「新規（{subject}と同じ）」の右に小さく） */
  subject: string;
  loading?: boolean;
  testId?: string;
}) {
  const L = useLabels();
  const og = useOngoing();
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const ongoing = og.on ? groups.filter((g) => g.isOngoing) : [];
  const regular = og.on ? groups.filter((g) => !g.isOngoing) : groups;
  const cur = value !== "new" && value !== "ongoing:new" ? (groups.find((g) => g.id === value) ?? null) : null;
  // 外を押す・Esc で閉じる（閉じたら欄にフォーカスを返す）
  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", down);
    return () => document.removeEventListener("pointerdown", down);
  }, [open]);
  useEffect(() => {
    if (open) box.current?.querySelector<HTMLButtonElement>(".prjopt[aria-checked='true'], .prjopt")?.focus();
  }, [open]);
  const pick = (v: ProjectChoice, g: GroupListItem | null) => {
    onChange(v, g);
    setOpen(false);
    setMore(false);
    btn.current?.focus();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      btn.current?.focus();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const opts = [...(box.current?.querySelectorAll<HTMLButtonElement>(".prjopt") ?? [])];
    const i = opts.indexOf(document.activeElement as HTMLButtonElement);
    e.preventDefault();
    opts[(i + (e.key === "ArrowDown" ? 1 : opts.length - 1)) % opts.length]?.focus();
  };
  const row = (g: GroupListItem) => (
    <button
      key={g.id}
      type="button"
      role="menuitemradio"
      aria-checked={value === g.id}
      className="prjopt"
      onClick={() => pick(g.id, g)}
      data-testid={`${testId}-opt`}
      data-group={g.id}
    >
      <span className="prjname">{g.name}</span>
      {g.completedAt ? <span className="chip">{ONGOING_MSG.completedChip}</span> : null}
      <span className="prjsub">{g.lastUsedOn ? ONGOING_MSG.lastUsed(md(g.lastUsedOn)) : null}</span>
      {value === g.id ? <Check /> : null}
    </button>
  );
  return (
    <div className="prjpick" ref={box} onKeyDown={onKey}>
      <button
        ref={btn}
        type="button"
        className="ppd-field prjfield"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={loading}
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
      >
        <span className="ppd-fieldlbl">{t(ONGOING_MSG.field)}</span>
        <span className="prjval" data-testid={`${testId}-value`}>
          {loading ? (
            <span className="muted">…</span>
          ) : value === "new" ? (
            t(ONGOING_MSG.pickNew)
          ) : value === "ongoing:new" ? (
            <>
              {ONGOING_MSG.newOngoingValue.replace("{name}", ongoingName)}
              <span className="chip ai">{og.short}</span>
            </>
          ) : cur ? (
            <>
              <span className="prjname">{cur.name}</span>
              {og.on && cur.isOngoing ? <span className="chip ai" data-testid={`${testId}-ongoing`}>{og.short}</span> : null}
              {cur.completedAt ? <span className="chip">{ONGOING_MSG.completedChip}</span> : null}
            </>
          ) : (
            <span className="muted">…</span>
          )}
        </span>
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="ppd-sicon">
          <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div className="prjmenu" role="menu" aria-label={t(ONGOING_MSG.field)} data-testid={`${testId}-menu`}>
          {ongoing.length ? (
            <>
              <div className="prjsec" role="presentation">{og.word}</div>
              {ongoing.map(row)}
              <div className="prjsep" role="separator" />
            </>
          ) : null}
          <button type="button" role="menuitem" className="prjopt" aria-expanded={more} onClick={() => setMore((v) => !v)} disabled={!regular.length} data-testid={`${testId}-active`}>
            <span className="prjname">{t(ONGOING_MSG.pickActive)}</span>
            <span className="prjsub">{ONGOING_MSG.activeCount(regular.length)}</span>
            {regular.length ? <Chevron open={more} /> : null}
          </button>
          {more ? <div className="prjsub-list">{regular.map(row)}</div> : null}
          <div className="prjsep" role="separator" />
          <button type="button" role="menuitemradio" aria-checked={value === "new"} className="prjopt" onClick={() => pick("new", null)} data-testid={`${testId}-new`}>
            <span className="prjname">{t(ONGOING_MSG.pickNew)}</span>
            <span className="prjsub">{subject.trim() || `（${L.subject}なし）`}</span>
            {value === "new" ? <Check /> : null}
          </button>
          {og.on ? (
            <button type="button" role="menuitemradio" aria-checked={value === "ongoing:new"} className="prjopt" onClick={() => pick("ongoing:new", null)} data-testid={`${testId}-ongoing-new`}>
              <Icon k="plus" />
              <span className="prjname">{t(ONGOING_MSG.pickCreateOngoing)}</span>
              <span className="prjsub">{ongoingName}</span>
              {value === "ongoing:new" ? <Check /> : null}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Check() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="prjcheck">
      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" className="prjcheck" style={{ transform: open ? "rotate(90deg)" : undefined }}>
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
